import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ThemeMode } from './themeMode';
import {
  readStoredThemeMode,
  storeThemeMode,
  systemPrefersDark,
  ThemeModeContext,
} from './themeMode';

/** Owns the light/dark/system choice and resolves `system` live. */
export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredThemeMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const onChange = (event: MediaQueryListEvent) =>
      setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === 'dark' || (mode === 'system' && systemDark),
      setMode: (next: ThemeMode) => {
        setModeState(next);
        storeThemeMode(next);
      },
    }),
    [mode, systemDark]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};
